import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { CalendarIcon } from '../../../icons/calendar';
import { cn } from '../../../lib/utils';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { utcIsoToZonedParts, zonedPartsToUtcIso } from './datetime-tz';

export interface DateTimePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  /**
   * UTC instant as ISO 8601 (e.g. `2026-05-01T21:00:00.000Z`). Empty string when
   * not set. This is the value persisted to the DB (`timestamptz`) — never a
   * wall-clock string.
   */
  value: string;
  /** Emits the UTC ISO instant, or `''` when cleared / no date picked yet. */
  onChange: (value: string) => void;
  /**
   * IANA timezone the wall-clock date+time is shown and edited in (e.g.
   * `America/Sao_Paulo`). The displayed time is the same for every viewer
   * regardless of their machine's local timezone.
   */
  timeZone: string;
  /** Inline error styling — border + ring turn destructive. */
  hasError?: boolean;
  disabled?: boolean;
  /** Latest allowed day (inclusive) in the calendar. */
  maxDate?: Date;
  /** Earliest allowed day (inclusive) in the calendar. */
  minDate?: Date;
  /** Year + month dropdowns in the calendar caption (default `true`). */
  captionDropdown?: boolean;
  /**
   * Wall-clock time (`HH:mm`) applied when a date is picked before any time is
   * set. Defaults to `'00:00'` (start of day). Pass `'23:59'` on an END-of-window
   * field so choosing only a date means "open through the END of that day"
   * instead of closing at its start. Once the user sets a time explicitly, that
   * time wins — this only fills the otherwise-empty initial pick.
   */
  defaultTime?: string;
}

function parseDatePartToLocal(datePart: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!m) return undefined;
  // Plain LOCAL date used only for the calendar's day rendering (day-granular,
  // tz-agnostic). The authoritative value stays the UTC instant in `value`.
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function localDateToPart(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/**
 * DateTimePicker — date + time editor whose VALUE is a UTC instant (ISO) but
 * whose DISPLAY/EDIT happens in a fixed `timeZone`. A native `<input type="date">`
 * (with our custom calendar on the icon) drives the day; a native
 * `<input type="time">` drives the time. Both recombine through `@date-fns/tz`
 * into the UTC instant on every change. Mirrors `DatePicker`'s look.
 */
export const DateTimePicker = forwardRef<HTMLInputElement, DateTimePickerProps>(
  (
    {
      value,
      onChange,
      timeZone,
      hasError = false,
      disabled = false,
      maxDate,
      minDate,
      captionDropdown = true,
      defaultTime = '00:00',
      className,
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const parts = utcIsoToZonedParts(value, timeZone);
    const selected = parseDatePartToLocal(parts.date);

    const now = new Date();
    const startMonth =
      minDate ?? (captionDropdown ? new Date(now.getFullYear() - 100, 0, 1) : undefined);
    const endMonth =
      maxDate ?? (captionDropdown ? new Date(now.getFullYear() + 10, 11, 31) : undefined);

    function emit(datePart: string, timePart: string) {
      onChange(zonedPartsToUtcIso(datePart, timePart, timeZone));
    }

    return (
      <div
        data-slot="date-time-picker"
        className={cn(
          'flex h-9 w-full items-center rounded-md border bg-background shadow-2xs transition-colors',
          hasError
            ? 'border-destructive focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-destructive'
            : 'border-input focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-primary',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <input
          ref={ref}
          type="date"
          value={parts.date}
          disabled={disabled}
          min={minDate ? localDateToPart(minDate) : undefined}
          max={maxDate ? localDateToPart(maxDate) : undefined}
          onChange={(e) => emit(e.target.value, parts.time || defaultTime)}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground outline-none',
            'disabled:cursor-not-allowed',
            '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden'
          )}
          {...rest}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Abrir calendário"
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none"
            >
              <CalendarIcon className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              captionLayout={captionDropdown ? 'dropdown' : 'label'}
              startMonth={startMonth}
              endMonth={endMonth}
              onSelect={(date) => {
                if (date) {
                  emit(localDateToPart(date), parts.time || defaultTime);
                  setOpen(false);
                }
              }}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
            />
          </PopoverContent>
        </Popover>
        <div className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />
        <input
          type="time"
          value={parts.time}
          disabled={disabled || !parts.date}
          aria-label="Horário"
          onChange={(e) => emit(parts.date, e.target.value)}
          className={cn(
            'w-[5.5rem] shrink-0 bg-transparent pr-3 text-sm text-foreground outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            '[&::-webkit-calendar-picker-indicator]:opacity-60'
          )}
        />
      </div>
    );
  }
);

DateTimePicker.displayName = 'DateTimePicker';
